var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Row, Col, Button, Modal, ModalFooter, MessageBanner, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { RecekTextField, RecekOnixItemPicker, RecekTextArea, RecekSelection, } from "../inputs";
import { HTTP_URL_REGEX } from "../../utils/validationUtils";
import css from "./FormModal.module.css";
var groupFieldsIntoRows = function (fields) {
    var _a, _b;
    var rows = [];
    var currentRow = [];
    var currentWidth = 0;
    for (var _i = 0, fields_1 = fields; _i < fields_1.length; _i++) {
        var field = fields_1[_i];
        var span = ((_a = field.colSpan) !== null && _a !== void 0 ? _a : 6) + (field.action ? ((_b = field.action.colSpan) !== null && _b !== void 0 ? _b : 3) : 0);
        if (currentWidth + span > 12 && currentRow.length > 0) {
            rows.push(currentRow);
            currentRow = [];
            currentWidth = 0;
        }
        currentRow.push(field);
        currentWidth += span;
        if (currentWidth >= 12) {
            rows.push(currentRow);
            currentRow = [];
            currentWidth = 0;
        }
    }
    if (currentRow.length > 0) {
        rows.push(currentRow);
    }
    return rows;
};
function FormSectionModal(_a) {
    var open = _a.open, isEditing = _a.isEditing, draft = _a.draft, onDraftChange = _a.onDraftChange, onSave = _a.onSave, onClose = _a.onClose, addLabelId = _a.addLabelId, editLabelId = _a.editLabelId, size = _a.size, fields = _a.fields, validate = _a.validate;
    var formatMessage = useIntl().formatMessage;
    var _b = useState(null), error = _b[0], setError = _b[1];
    var _c = useState({}), fieldErrors = _c[0], setFieldErrors = _c[1];
    useEffect(function () {
        if (open) {
            setError(null);
            setFieldErrors({});
        }
    }, [open]);
    var validateFields = useCallback(function (draft) {
        var errors = {};
        for (var _i = 0, fields_2 = fields; _i < fields_2.length; _i++) {
            var field = fields_2[_i];
            if (field.type === "url") {
                var val = draft[field.key] || "";
                if (val.trim() && !HTTP_URL_REGEX.test(val.trim())) {
                    errors[field.key] = formatMessage({
                        id: "ui-br-agency.title.validation.resourceLink.web",
                    });
                }
            }
        }
        return errors;
    }, [fields, formatMessage]);
    var handleSave = useCallback(function () {
        var fErrors = validateFields(draft);
        if (Object.keys(fErrors).length > 0) {
            setFieldErrors(fErrors);
            return;
        }
        setFieldErrors({});
        var validationResult = validate(draft);
        if (validationResult !== true) {
            var messageId = typeof validationResult === "string"
                ? validationResult
                : "ui-br-agency.title.form.error.required";
            setError(formatMessage({ id: messageId }));
            return;
        }
        setError(null);
        onSave();
    }, [validateFields, validate, draft, formatMessage, onSave]);
    var modalLabel = isEditing
        ? formatMessage({ id: editLabelId })
        : formatMessage({ id: addLabelId });
    var footer = useMemo(function () { return (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", onClick: handleSave },
            React.createElement(FormattedMessage, { id: "ui-br-agency.button.save" })),
        React.createElement(Button, { buttonStyle: "default", onClick: onClose },
            React.createElement(FormattedMessage, { id: "ui-br-agency.button.cancel" })))); }, [handleSave, onClose]);
    var handleInputChange = useCallback(function (key) { return function (e) {
        onDraftChange(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[key] = e.target.value, _a)));
        });
    }; }, [onDraftChange]);
    var handleTextAreaChange = useCallback(function (key) { return function (e) {
        onDraftChange(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[key] = e.target.value, _a)));
        });
    }; }, [onDraftChange]);
    var handleOnixChange = useCallback(function (key) { return function (item) {
        onDraftChange(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[key] = item, _a)));
        });
    }; }, [onDraftChange]);
    var renderField = function (field) {
        var _a, _b, _c, _d;
        var value = draft[field.key];
        switch (field.type) {
            case "onix":
                return (React.createElement(RecekOnixItemPicker, { labelId: field.labelId, dataOptions: (_a = field.dataOptions) !== null && _a !== void 0 ? _a : [], value: value, onChange: handleOnixChange(field.key), required: field.required, loading: field.loading, codetableStatus: field.codetableStatus, disabled: field.disabled }));
            case "textarea":
                return (React.createElement(RecekTextArea, __assign({ labelId: field.labelId, value: value || "", onChange: handleTextAreaChange(field.key), required: field.required }, field.inputProps)));
            case "selection": {
                var objValue = value;
                var selectedId = field.valueKey
                    ? ((_b = objValue === null || objValue === void 0 ? void 0 : objValue[field.valueKey]) !== null && _b !== void 0 ? _b : "")
                    : ((_c = objValue === null || objValue === void 0 ? void 0 : objValue.id) !== null && _c !== void 0 ? _c : "");
                return (React.createElement(RecekSelection, { labelId: field.labelId, dataOptions: (_d = field.selectOptions) !== null && _d !== void 0 ? _d : [], asyncFilter: true, onFilter: field.onFilter, value: selectedId, onChange: field.onSelect, required: field.required, disabled: field.disabled }));
            }
            case "url":
                return (React.createElement(RecekTextField, __assign({ labelId: field.labelId, value: value || "", onChange: handleInputChange(field.key), required: field.required, error: fieldErrors[field.key] }, field.inputProps)));
            case "text":
            default: {
                var textValue = field.valueFormatter
                    ? field.valueFormatter(draft)
                    : value || "";
                return (React.createElement(RecekTextField, __assign({ labelId: field.labelId, value: textValue, onChange: handleInputChange(field.key), required: field.required, disabled: field.disabled }, field.inputProps)));
            }
        }
    };
    var rows = useMemo(function () { return groupFieldsIntoRows(fields); }, [fields]);
    return (React.createElement(Modal, { open: open, onClose: onClose, label: modalLabel, footer: footer, size: size, dismissible: true },
        error && (React.createElement(MessageBanner, { type: "error", className: css.errorBanner }, error)),
        rows.map(function (row, rowIndex) { return (React.createElement(Row, { key: rowIndex }, row.map(function (field) {
            var _a, _b, _c;
            return (React.createElement(React.Fragment, { key: (_a = field.id) !== null && _a !== void 0 ? _a : field.key },
                React.createElement(Col, { xs: (_b = field.colSpan) !== null && _b !== void 0 ? _b : 6 }, renderField(field)),
                field.action && (React.createElement(Col, { xs: (_c = field.action.colSpan) !== null && _c !== void 0 ? _c : 3, className: css.fieldActionCol },
                    React.createElement(Button, { buttonStyle: "default", marginBottom0: true, onClick: field.action.onClick },
                        React.createElement(FormattedMessage, { id: field.action.labelId }))))));
        }))); })));
}
export { FormSectionModal };
